import {
  ArticleHelpfulState,
  ArticleType,
  SourceType,
} from '@wix/answers-api';
import { clickTheWasItHelpfulButtons } from '@wix/bi-logger-new-help-center/v2';
import { Box, Button, IconButton, Text } from '@wix/design-system';
import { useFedops } from '@wix/fe-essentials-standalone';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  ThumbsDownSmall,
  ThumbsUpSmall,
  X as XIcon,
} from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  BI,
  CHATBOT_REFERRALS,
  FEDOPS,
  LOCAL_STORAGE_PREFIX,
} from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { useBI } from '../../../../hooks/useBI';
import {
  ItemOnPageEventSource,
  useBiOnVisibleItem,
} from '../../../../hooks/useBiOnVisibleItem';
import { useMaxScrollPercentage } from '../../../../hooks/useMaxScrollPercentage';
import { answersApi } from '../../../../utils/answers';
import { mapArticleTypeToItemType } from '../../../../utils/bi';
import {
  getSessionStorage,
  setSessionStorage,
} from '../../../../utils/web-storage-wrapper';
import { useStore } from '../../../../zustand/stores';
import { CopilotDisplayMode } from '../../../../zustand/stores/copilot';
import { getUserSpecificStorageKey } from '../../../ArticleFeedback';
import { ThankYouMessage } from '../../../ArticleFeedback/ThankYouMessage';

import css from './index.module.scss';

export type ArticleFeedbackBarProps = {
  articleId: string;
  onExpandRightPanel: () => void;
  scrollContainerRef?: RefObject<HTMLElement>;
  isChatbotEpHidden?: boolean;
};

const THANK_YOU_AUTO_HIDE_MS = 3000;
const SCROLL_THRESHOLD_PERCENT = 60;

export const ArticleFeedbackBar: FunctionComponent<ArticleFeedbackBarProps> = ({
  articleId,
  onExpandRightPanel,
  scrollContainerRef,
  isChatbotEpHidden = false,
}: ArticleFeedbackBarProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { user } = useStore((state) => state.userSlice);
  const { setCopilotDisplayMode, setChatbotReferral } = useStore(
    (state) => state.copilotSlice,
  );
  const { sendBIEvent } = useBI();
  const fedopsApi = useFedops();
  const feedbackDetails = BI.FEEDBACK_DETAILS.OLD;

  const [vote, setVote] = useState<ArticleHelpfulState>(
    ArticleHelpfulState.NONE,
  );
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [maxScrollPercentage, isScrollable] = useMaxScrollPercentage(
    scrollContainerRef as RefObject<HTMLElement>,
    articleId,
  );
  const hasScrolledEnough =
    !scrollContainerRef ||
    !isScrollable ||
    maxScrollPercentage >= SCROLL_THRESHOLD_PERCENT;
  const [centerLeft, setCenterLeft] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el || typeof window === 'undefined') {
      return;
    }
    const updateCenter = () => {
      const rect = el.getBoundingClientRect();
      setCenterLeft(rect.left + rect.width / 2);
    };
    updateCenter();
    const resizeObserver = new ResizeObserver(updateCenter);
    resizeObserver.observe(el);
    window.addEventListener('resize', updateCenter);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCenter);
    };
  }, [scrollContainerRef]);

  const { itemRef, cancelItemOnPageEvent } = useBiOnVisibleItem<HTMLDivElement>(
    ItemOnPageEventSource.HELPFUL_VOTE_OPTION,
    {
      item_id: articleId,
      feedback_details: feedbackDetails,
    },
  );

  const restoreFromStorage = useCallback(() => {
    const storageKey = getUserSpecificStorageKey(
      LOCAL_STORAGE_PREFIX.FEEDBACK_VOTE,
      articleId,
      user !== null,
    );
    const stored = getSessionStorage(storageKey);
    if (stored) {
      setIsHidden(true);
      cancelItemOnPageEvent();
      setVote(
        JSON.parse(stored)
          ? ArticleHelpfulState.HELPFUL
          : ArticleHelpfulState.NOT_HELPFUL,
      );
    } else {
      setVote(ArticleHelpfulState.NONE);
      setIsHidden(false);
    }
  }, [articleId, user, cancelItemOnPageEvent]);

  useEffect(() => {
    restoreFromStorage();
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
    };
  }, [restoreFromStorage]);

  const persistVote = (submittedVote: ArticleHelpfulState) => {
    const storageKey = getUserSpecificStorageKey(
      LOCAL_STORAGE_PREFIX.FEEDBACK_VOTE,
      articleId,
      user !== null,
    );
    const value =
      submittedVote === ArticleHelpfulState.NOT_HELPFUL
        ? String(false)
        : String(true);
    setSessionStorage(storageKey, value);
  };

  const sendVote = async (submittedVote: ArticleHelpfulState) => {
    fedopsApi.interactionStarted(FEDOPS.INTERACTION_NAMES.HELPFUL_ARTICLE);
    await answersApi.articles.sendFeedback({
      sourceType: SourceType.HELP_CENTER,
      sourceId: null,
      id: articleId,
      newState: submittedVote,
      locale: locale as string,
    });
    fedopsApi.interactionEnded(FEDOPS.INTERACTION_NAMES.HELPFUL_ARTICLE);

    await sendBIEvent(
      clickTheWasItHelpfulButtons({
        source_name: BI.SOURCE_NAMES.ARTICLE,
        kb_lang: locale as string,
        item_id: articleId,
        item_type: mapArticleTypeToItemType(ArticleType.ARTICLE),
        thumbs_up: submittedVote === ArticleHelpfulState.HELPFUL,
        feedback_details: feedbackDetails,
      }),
    );
  };

  const handleHelpful = async () => {
    setVote(ArticleHelpfulState.HELPFUL);
    persistVote(ArticleHelpfulState.HELPFUL);
    await sendVote(ArticleHelpfulState.HELPFUL);
    autoHideTimeoutRef.current = setTimeout(() => {
      setIsHidden(true);
    }, THANK_YOU_AUTO_HIDE_MS);
  };

  const handleNotHelpful = async () => {
    setVote(ArticleHelpfulState.NOT_HELPFUL);
    persistVote(ArticleHelpfulState.NOT_HELPFUL);
    setIsHidden(true);
    if (!isChatbotEpHidden) {
      setChatbotReferral(CHATBOT_REFERRALS.UNHELPFUL_ARTICLE);
      setCopilotDisplayMode(CopilotDisplayMode.CopilotMainView);
      setTimeout(() => {
        setCopilotDisplayMode(CopilotDisplayMode.UnhelpfulFeedback);
      }, 0);
      onExpandRightPanel();
    }
    await sendVote(ArticleHelpfulState.NOT_HELPFUL);
  };

  const handleClose = () => {
    setIsHidden(true);
    cancelItemOnPageEvent();
  };

  if (isHidden || !hasScrolledEnough) {
    return null;
  }

  const showThankYou = vote === ArticleHelpfulState.HELPFUL;

  return (
    <Box
      ref={itemRef}
      dataHook={DATA_HOOKS.ARTICLE_FEEDBACK_BAR}
      className={classNames(css.bar, {
        [css.thankYou]: showThankYou,
      })}
      align="center"
      verticalAlign="middle"
      style={centerLeft !== undefined ? { left: centerLeft } : undefined}
    >
      {showThankYou ? (
        <ThankYouMessage />
      ) : (
        <>
          <Text
            size="small"
            weight="bold"
            className={css.title}
            dataHook={DATA_HOOKS.ARTICLE_FEEDBACK_BAR_TITLE}
          >
            {t('feedback.v2.helpful.title')}
          </Text>
          <Box direction="horizontal" className={css.actions}>
            <Button
              size="small"
              skin="dark"
              priority="secondary"
              prefixIcon={<ThumbsUpSmall />}
              onClick={handleHelpful}
              dataHook={DATA_HOOKS.ARTICLE_FEEDBACK_BAR_YES}
              className={css.voteButton}
            >
              {t('helpful.button.yes')}
            </Button>
            <Button
              size="small"
              skin="dark"
              priority="secondary"
              prefixIcon={<ThumbsDownSmall />}
              onClick={handleNotHelpful}
              dataHook={DATA_HOOKS.ARTICLE_FEEDBACK_BAR_NO}
              className={css.voteButton}
            >
              {t('helpful.button.no')}
            </Button>
            <IconButton
              size="tiny"
              skin="dark"
              priority="secondary"
              onClick={handleClose}
              dataHook={DATA_HOOKS.ARTICLE_FEEDBACK_BAR_CLOSE}
              className={css.closeButton}
            >
              <XIcon />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
};
